import { memo } from "react";

import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { Avatar, Stack, Tooltip, Typography } from "@mui/material";

import { EVOTRUX_BLUE, EVOTRUX_BLUE_LIGHT } from "constants/index";
import { getInitialsFromName } from "utils";

import { CheckableRow } from "./CheckableRow";
import { getTeammateBranchName, getTeammateFullName } from "./utils";

interface Props {
  teammate: CompanyMember;
  isInMyBranch: boolean;
  isShared: boolean;
  isSelected: boolean;
  onToggle: (roleId: number) => void;
}

export const TeammateRow = memo((props: Props) => {
  const { teammate, isInMyBranch, isShared, isSelected, onToggle } = props;
  const fullName = getTeammateFullName(teammate);

  return (
    <CheckableRow
      checked={isSelected}
      selected={isSelected}
      ariaLabel={
        isShared ? `Share again with ${fullName}` : `Select ${fullName}`
      }
      onClick={() => onToggle(teammate.id)}
    >
      <Avatar
        aria-hidden
        sx={{
          width: (theme) => theme.spacing(4),
          height: (theme) => theme.spacing(4),
          typography: "caption",
          fontWeight: "fontWeightMedium",
          bgcolor: isInMyBranch ? EVOTRUX_BLUE_LIGHT : "grey.200",
          color: isInMyBranch ? EVOTRUX_BLUE : "text.secondary",
        }}
      >
        {getInitialsFromName(fullName)}
      </Avatar>
      <Stack flex={1} minWidth={0}>
        <Typography variant="body2" noWrap>
          {fullName}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          color="text.secondary"
        >
          <BusinessOutlinedIcon fontSize="inherit" />
          <Typography variant="caption" noWrap>
            {getTeammateBranchName(teammate)}
          </Typography>
        </Stack>
      </Stack>
      {isShared && (
        <Tooltip title="You already shared this template with this teammate. You can share it with them again.">
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            color="success.main"
          >
            <CheckOutlinedIcon fontSize="small" />
            <Typography variant="caption" fontWeight="fontWeightBold">
              {isSelected ? "Share again" : "Shared"}
            </Typography>
          </Stack>
        </Tooltip>
      )}
    </CheckableRow>
  );
});
